import type { Currency } from '../../../constants';
import { OrderStatus, type DeliveryTrackingStatus, type ParxlTrackingStatus, type YunExpressTrackingStatus } from '../../../constants/order';

export type TrackingStatusType = DeliveryTrackingStatus | ParxlTrackingStatus | YunExpressTrackingStatus;

export interface DeleoTraceEvent {
  detailStatus: string | null;
  eventTime: string;
  description: string;
  location: string | null;
  empNm: string | null;
  status: DeliveryTrackingStatus;
}

export interface TrackingEvent {
  id: string;
  type: TrackingStatusType;
  title: string;
  timestamp: string;
  description?: string;
  status: 'completed' | 'current' | 'pending';
}

export interface OrderLineItem {
  itemId: string;
  productId?: string;
  parentProductId?: string;
  name: string;
  nameKor?: string;
  originalName?: string;
  sku: string;
  quantity: number;
  price?: number;
  total?: number;
  subtotal?: number;
  productSize?: string;
  productPack?: string;
  wooCommerceImageUrl?: string;
  inboundStatus?: string;
  sourcingRoute?: string;
  deliveryCompanyCd?: string;
  brand?: string;
  category?: string;
}

export interface OrderAddress {
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  phone?: string;
  email?: string;
}

export interface DeleoData {
  deleoOrderId: string;
  deleoTrackingNo: string;
  deleoStatus: {
    name: string;
    code: string;
    desc: string;
  };
  deliveryTrace?: {
    orderId: string;
    status: {
      name: string;
      code: string;
      desc: string;
    };
    traceInfoList: Array<{
      mainTrackingNo: string;
      trackingNo: string;
      description: string;
      status: string;
      origin: string;
      dest: string;
      deliveryCompanyCode: {
        name: string;
        code: string;
        desc: string;
      };
      traceList: DeleoTraceEvent[];
    }>;
  };
  orderTrackingList?: Array<{
    trackingNo: string;
    deleoTrackingNo: string;
    deliveryCompanyCd: {
      name: string;
      code: string;
      desc: string;
    };
  }>;
  orderItemList?: Array<{
    orderItemId: string;
    itemCd: string;
    itemNm: string;
    quantity: number;
    amt: number;
    status: {
      name: string;
      code: string;
      desc: string;
    };
  }>;
}

export interface ParxlTrackingEvent {
  status: ParxlTrackingStatus;
  date: string;
  location: string;
}

export interface ParxlData {
  plsShipmentReference?: string;
  merchantBatchNo?: string;
  merchantOrderNo?: string;
  orderDate?: string;
  operationHub?: string;
  serviceOption?: string;
  latestTrackingStatus?: string;
  trackingStatus?: ParxlTrackingEvent[];
  weight?: number;
  weightUnit?: string;
  dimensions?: {
    length: number;
    width: number;
    height: number;
    unit: string;
  };
  incoterm?: string;
  containsBattery?: boolean;
  containsDg?: boolean;
  insured?: boolean;
  cod?: boolean;
  expirationDate?: string;
}

export interface YunExpressTrackEvent {
  processTime: string;
  processUtcTime: string;
  processContent: string;
  processCountry: string;
  processProvince: string;
  processCity: string;
  processLocation: string;
  trackNodeCode: string;
  trackNodeDescription?: string;
}

export interface YunExpressTrackInfo {
  waybillNumber?: string;
  trackingNumber?: string;
  customerOrderNumber?: string;
  trackEvents: YunExpressTrackEvent[];
}

export interface YunExpressData {
  waybillNumber: string;
  customerOrderNumber?: string;
  lastPackageStatus?: string;
  lastSyncedAt?: string;
  trackInfo?: YunExpressTrackInfo;
}

export type ShippingProvider = 'DELEO' | 'PARXL' | 'YUN_EXPRESS';

export type ShippingStatus = 'PENDING' | 'IN_TRANSIT' | 'DELIVERED' | 'FAILED';

export interface ShippingData {
  provider: ShippingProvider;
  providerOrderId?: string;
  trackingNumber?: string;
  status?: ShippingStatus;
  statusCode?: string;
  statusDescription?: string;
  statusUpdatedAt?: string;
  labelUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Order {
  orderId: string;
  status: OrderStatus;
  currency: Currency;
  totalAmount: number;
  discount?: number;
  orderDate: string;
  orderDateTime: string;
  createdAt: string;
  updatedAt: string;
  customerId: string;
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  lineItems: OrderLineItem[];
  shippingAddress: OrderAddress;
  billingAddress: OrderAddress;
  deleoData?: DeleoData;
  parxlData?: ParxlData;
  yunExpressData?: YunExpressData;
  shippingData?: ShippingData;
  shippingMessage?: string;
  pic?: string;
}

export type GetOrderRequest = {
  orderId: number;
  sessionKey?: string;
};

export type GetOrderResponse = Order;

export type GetOrdersRequest = {
  page?: number;
  perPage?: number;
  status?: OrderStatus;
  sessionKey?: string;
  customerId?: number;
};

export type GetOrdersResponse = {
  orders: Order[];
  total: number;
  totalPages: number;
};

export type GetUserOrdersRequest = {
  userId: number;
};

export type GetUserOrdersResponse = {
  orderIds: string[];
};

export type AssociateOrderRequest = {
  orderId: number;
};

export type AssociateOrderData = {
  orderId: number;
  userId: number;
  associatedAt: string;
  order: {
    id: number;
    status: string;
    total: string;
    currency: string;
    createdAt: string;
  };
};

export type AssociateOrderResponse = {
  success: boolean;
  message: string;
  data: AssociateOrderData;
};

/**
 * Edit shipping address request - for updating shipping info after order creation
 * Used for Express Checkout when required fields are missing
 */
export type EditShippingAddressRequest = {
  orderId: number;
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  phone: string;
};

export type EditShippingAddressResponse = {
  success: boolean;
  message?: string;
};

/**
 * Edit billing address request - for updating billing info after order creation
 * Used for Express Checkout to update billing address with payer data
 * Note: Orders can only be edited within 5 minutes of creation
 */
export type EditBillingAddressRequest = {
  orderId: number;
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  phone?: string;
  email?: string;
};

export type EditBillingAddressResponse = {
  message: string;
};
